import React, { useEffect, useState } from "react";
import { Button, Modal, Image, Icon, Input, Form, Dropdown, Radio, Header, HeaderSubheader, Message, FormInput, FormGroup, FormDropdown, FormRadio, FormSelect } from "semantic-ui-react";
import { Button as CustomButton } from "../atoms/Button";

// Components
import { EmailSubjectInput, EmailBodyInput, UploadImageFormField } from "../atoms/FormFields";
// Constants
import i18nstring, { strings } from "../../i18n";
import Chat from "../../images/icons/chat.png";

// Theme
import "./OrganismStyles.scss";
import { Roles } from "../../Shared/Constants/Roles";
import { Colors } from "../../constants/Styles";
import { BadgeType } from "../../Shared/Constants/Badge";
import DatePicker from "react-datepicker";

import "../atoms/AtomStyles.scss";
import { DonationCategory, DonationType } from "../../constants/types";
import ConfirmationPopup from "../atoms/ConfirmationPopup";

const modalStyle = {
  width: 900
};

// Roles a badge can be authored for.
export const BADGE_ACCOUNT_TYPES = {
  [Roles.PRODUCER]: "Gardener",
  [Roles.COURIER]: "Courier"
};

/**
 * Which account types each badge type may be awarded to. Everything is
 * gardener-only today — donation badges are counted off a gardener's delivered
 * donations, and only gardeners can set goals — so the field offers no choice
 * and renders disabled.
 *
 * The plumbing underneath is already role-generic (goals carry an `ownerRole`,
 * badge award matches on `accountType`), so enabling courier goal badges is
 * adding `Roles.COURIER` to the line below and nothing else.
 */
const ACCOUNT_TYPES_BY_BADGE_TYPE = {
  [BadgeType.DONATION]: [Roles.PRODUCER],
  [BadgeType.GOAL_COMPLETED]: [Roles.PRODUCER]
};

export const accountTypesForBadgeType = type =>
  ACCOUNT_TYPES_BY_BADGE_TYPE[type] || [Roles.PRODUCER];

/** "GOAL_COMPLETED" -> "Goal Completed" */
export const badgeTypeLabel = type =>
  (type || "")
    .split("_")
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

export function GroupMessageModal({
  selectedUsers,
  sendEmailMultiple,
  sendEmailToGroup,
  groupName
}) {
  const [inputs, setInputs] = useState({ messageBody: "", messageSubject: "" });
  const [toggleModal, setToggle] = useState(false);
  const allSelected = selectedUsers["APPROVED"] || selectedUsers["PENDING"];
  const handleInputChange = event => {
    event.persist();
    setInputs(inputs => ({
      ...inputs,
      [event.target.name]: event.target.value
    }));
  };
  const sendMailToGroup = () => {
    if (allSelected) {
      sendEmailToGroup(inputs.messageBody, inputs.messageSubject);
    } else {
      const emailsToSend = Object.keys(selectedUsers).filter(
        userId =>
          userId !== "PENDING" && userId !== "APPROVED" && selectedUsers[userId]
      );
      sendEmailMultiple(
        emailsToSend,
        inputs.messageBody,
        inputs.messageSubject
      );
    }
    setToggle(false);
  };
  let clientFriendlyGroupName = "";
  switch (groupName) {
    case Roles.RECIPIENT:
      clientFriendlyGroupName = " All Nonprofits";
      break;
    case Roles.COURIER:
      clientFriendlyGroupName = " All Couriers in your Nonprofit";
      break;
    case Roles.PRODUCER:
      clientFriendlyGroupName = " All Gardners in your Nonprofit";
      break;
    default:
      console.warn("unhandled role");
      break;
  }

  return (
    <Modal
      style={modalStyle}
      open={toggleModal}
      trigger={
        <div
          className="table_header_action_button"
          onClick={() => setToggle(true)}
        >
          Message
        </div>
      }
    >
      <Modal.Header>
        {i18nstring(strings.SEND_EMAIL)}{" "}
        {allSelected ? clientFriendlyGroupName : "multiple users"}
      </Modal.Header>
      <Modal.Content>
        <EmailSubjectInput
          name="messageSubject"
          placeholder={i18nstring(strings.EMAIL_SUBJECT)}
          onChange={handleInputChange}
          width={"100%"}
          maxChar={100}
          value={inputs.messageSubject}
        />
        <EmailBodyInput
          name="messageBody"
          placeholder={i18nstring(strings.EMAIL_BODY)}
          onChange={handleInputChange}
          width={"100%"}
          maxChar={300}
          value={inputs.messageBody}
        />
      </Modal.Content>
      <Button
        style={{ marginBottom: "2.5%", marginLeft: "2.5%" }}
        negative
        floated="left"
        onClick={() => setToggle(false)}
      >
        cancel
      </Button>
      <Button
        style={{ marginBottom: "2.5%", marginRight: "2.5%" }}
        positive
        floated="right"
        onClick={sendMailToGroup}
      >
        Send
      </Button>
    </Modal>
  );
}

export function MessageModal({ sendEmail, userEmail, userId, compact = false }) {
  const [inputs, setInputs] = useState({ messageBody: "", messageSubject: "" });
  const [toggleModal, setToggle] = useState(false);
  const handleInputChange = event => {
    event.persist();
    setInputs(inputs => ({
      ...inputs,
      [event.target.name]: event.target.value
    }));
  };
  const send = () => {
    sendEmail(userId, inputs.messageBody, inputs.messageSubject);
    setToggle(false);
  };
  const trigger = compact ? (
    <div className="review_action_btn" onClick={() => setToggle(true)}>
      <Icon name="comments" size="large" />
    </div>
  ) : (
    <div className="table_cell_action" onClick={() => setToggle(true)}>
      <Image src={Chat} className="chat_image" />
    </div>
  );
  return (
    <Modal
      style={modalStyle}
      open={toggleModal}
      trigger={trigger}
    >
      <Modal.Header>
        {i18nstring(strings.SEND_EMAIL)} {userEmail}
      </Modal.Header>
      <Modal.Content>
        <EmailSubjectInput
          name="messageSubject"
          placeholder={i18nstring(strings.EMAIL_SUBJECT)}
          onChange={handleInputChange}
          width={"100%"}
          maxChar={100}
          value={inputs.messageSubject}
        />
        <EmailBodyInput
          name="messageBody"
          placeholder={i18nstring(strings.EMAIL_BODY)}
          onChange={handleInputChange}
          width={"100%"}
          maxChar={300}
          value={inputs.messageBody}
        />
      </Modal.Content>
      <Button
        style={{ marginBottom: "2.5%", marginLeft: "2.5%" }}
        negative
        floated="left"
        onClick={() => setToggle(false)}
      >
        Cancel
      </Button>
      <Button
        style={{ marginBottom: "2.5%", marginRight: "2.5%" }}
        positive
        floated="right"
        onClick={send}
      >
        Send
      </Button>
    </Modal>
  );
}

export function DialogueModal(props) {
  return (
    <Modal
      defaultOpen
      onActionClick={props.toggleModal}
      style={{ ...modalStyle, width: "30%" }}
    >
      <Modal.Header>{props.title}</Modal.Header>
      <Modal.Content style={{ alignItems: "flex-end" }}>
        <div className="content_message">{props.message}</div>
      </Modal.Content>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: props.cancelModal ? "space-between" : "flex-end",
          alignItems: "flex-end",
          padding: "2.3pt",
          height: "40pt",
          width: "100%",
          alignSelf: "center"
        }}
      >
        {props.cancelModal && (
          <CustomButton onClick={props.cancelModal} color={Colors.lightGrey}>
            {i18nstring(strings.CANCEL)}
          </CustomButton>
        )}
        <CustomButton onClick={props.toggleModal}>
          {i18nstring(strings.CONFIRM)}
        </CustomButton>
      </div>
    </Modal>
  );
}


export function BadgeModal({
  badge,
  createBadge,
  toggleModal,
  setToggle,
  editBadge
}) {
  const [icon, setIcon] = useState(null);
  const [url, setUrl] = useState(null);
  const [isValidationClicked, setIsValidationClicked] = useState(false);

  const [formData, setFormData] = useState({
    titleEn: "",
    titleEs: "",
    descriptionEn: "",
    descriptionEs: "",
    type: "",
    accountType: Roles.PRODUCER,
    value: "",
    isActive: true
  });
  useEffect(() => {
    setIsValidationClicked(false)
    if (!badge) setIcon(null);
    else {
      setIcon(null);
      setUrl(badge.iconUrl);
      setFormData({
        titleEn: badge?.title?.['en-US'] || "",
        titleEs: badge?.title?.['es-US'] || "",
        descriptionEn: badge?.description?.['en-US'] || "",
        descriptionEs: badge?.description?.['es-US'] || "",
        type: badge ? badge.type : "",
        accountType: badge?.accountType || Roles.PRODUCER,
        value: badge?.value || "",
        isActive: badge.isActive === true ? true : false
      });
    }
  }, [badge]);

  const title = badge
    ? i18nstring(strings.EDIT_BADGE)
    : i18nstring(strings.ADD_BADGE);
  const typeList = Object.keys(BadgeType).map((type, index) => {
    return {
      key: index,
      text: badgeTypeLabel(BadgeType[type]),
      value: BadgeType[type]
    };
  });
  const allowedAccountTypes = accountTypesForBadgeType(formData.type);
  const accountTypeList = allowedAccountTypes.map((role, index) => {
    return {
      key: index,
      text: BADGE_ACCOUNT_TYPES[role],
      value: role
    };
  });

  // `value` is always "how many of the thing you need", but what the thing is
  // depends on the badge type, so the field is labelled accordingly.
  const valueLabel =
    {
      [BadgeType.DONATION]: i18nstring(strings.BADGE_VALUE_DONATIONS),
      [BadgeType.GOAL_COMPLETED]: i18nstring(strings.BADGE_VALUE_GOALS)
    }[formData.type] || i18nstring(strings.BADGE_VALUE);

  const handleChange = (e, { name, value }) => {
    e.stopPropagation();
    const next = { ...formData, [name]: value };
    if (name === "type") {
      // Goal badges are one-per-completed-goal, so 1 is the only value that
      // makes sense for the first tier — prefill it rather than making admins
      // guess.
      if (value === BadgeType.GOAL_COMPLETED && formData.value === "") {
        next.value = 1;
      }
      // Switching type can invalidate the account type (donation badges are
      // gardener-only), so snap it back to a type the new badge allows.
      const allowed = accountTypesForBadgeType(value);
      if (!allowed.includes(next.accountType)) {
        next.accountType = allowed[0];
      }
    }
    setFormData(next);
  };

  const resetFormData = () => {
    setFormData({
      titleEn: "",
      titleEs: "",
      descriptionEn: "",
      descriptionEs: "",
      type: "",
      accountType: Roles.PRODUCER,
      value: "",
      isActive: true
    });
    setIcon(null);
    setIsValidationClicked(false);
    setToggle(false)
  };

  const submitRequest = async () => {
    if (!validateInput()) { 
      setIsValidationClicked(true);
      return;
    }
    const title = {
      'en-US': formData.titleEn,
      'es-US': formData.titleEs
    };
    const description = {
      'en-US': formData.descriptionEn,
      'es-US': formData.descriptionEs
    };
    const data = {
      ...(badge && { ...badge }),
      badgeId: badge
        ? badge.badgeId
        : formData.type?.toLowerCase() + "-" + formData.value,
      type: formData.type,
      value: Number(formData.value),
      // enforced here rather than trusted from form state, so a donation badge
      // can never be saved against anything but a gardener
      accountType: allowedAccountTypes.includes(formData.accountType)
        ? formData.accountType
        : allowedAccountTypes[0],
      title: title,
      description: description,
      isActive: formData.isActive
    };
    if (badge) {
      editBadge(badge.id, data, icon);
    } else {
      createBadge(data, icon);
    }
    resetFormData();
    setToggle(false);
  };

  const fieldLable = value => (
    <label className="badge-input-label">{value}</label>
  );

  const validateInput = () => {
    if (
      formData.titleEn.trim() === "" ||
      formData.titleEs.trim() === "" ||
      formData.descriptionEn.trim() === "" ||
      formData.descriptionEs.trim() === "" ||
      formData.type === '' ||
      formData.accountType === '' ||
      formData.value == '' || formData.value < 0
    ) {
      return false;
    }
    return true;
  };

  return (
    <Modal style={modalStyle} open={toggleModal}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        <Form>
          <div className="badge-input-parent">
            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.TITLE_EN))}
              <Input
                error={isValidationClicked && formData.titleEn === ''}
                name="titleEn"
                placeholder={i18nstring(strings.TITLE_PLACEHOLDER)}
                value={formData.titleEn}
                onChange={handleChange}
                style={{ width: "100%" }}
              />
            </div>

            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.TITLE_ES))}
              <Input
                name="titleEs"
                error={isValidationClicked &&  formData.titleEs === ''}
                placeholder={i18nstring(strings.TITLE_PLACEHOLDER)}
                value={formData.titleEs}
                onChange={handleChange}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="badge-input-parent">
            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.DESCRIPTION_EN))}
              <Form.TextArea
                name="descriptionEn"
                error={isValidationClicked && formData.descriptionEn === ''}
                placeholder={i18nstring(strings.DESCRIPTION_PLACEHOLDER)}
                value={formData.descriptionEn}
                onChange={handleChange}
                style={{ width: "100%" }}
              />
            </div>

            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.DESCRIPTION_ES))}
              <Form.TextArea
                name="descriptionEs"
                error={isValidationClicked && formData.descriptionEs === ''}
                placeholder={i18nstring(strings.DESCRIPTION_PLACEHOLDER)}
                value={formData.descriptionEs}
                onChange={handleChange}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="badge-input-parent">
            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.BADGE_TYPE))}
              <Dropdown
                name="type"
                error={isValidationClicked && formData.type === ''}
                placeholder={i18nstring(strings.SELECT_BADGE_TYPE)}
                fluid
                selection
                options={typeList}
                value={formData.type}
                onChange={handleChange}
                style={{ width: "100%" }}
                disabled={badge ? true : false}
              />
            </div>

            <div className="badge-input-container">
              {fieldLable(valueLabel)}
              <Input
                type="number"
                error={isValidationClicked && formData.value === ''}
                placeholder={i18nstring(strings.VALUE_PLACEHOLDER)}
                name="value"
                value={formData.value}
                onChange={handleChange}
                style={{ width: "100%" }}
                disabled={badge ? true : false}
              />
            </div>
          </div>

          <div className="badge-input-parent">
            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.BADGE_ACCOUNT_TYPE))}
              {/* only one valid choice (donation badges are gardener-only), so
                  show it rather than offering a pointless decision */}
              <Dropdown
                name="accountType"
                error={isValidationClicked && formData.accountType === ''}
                placeholder={i18nstring(strings.SELECT_ACCOUNT_TYPE)}
                fluid
                selection
                options={accountTypeList}
                value={formData.accountType}
                onChange={handleChange}
                style={{ width: "100%" }}
                disabled={Boolean(badge) || allowedAccountTypes.length < 2}
              />
            </div>

            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.BADGE_ICON))}
              {!icon && !url ? (
                <UploadImageFormField
                  width={"100%"}
                  style={{ width: "100%" }}
                  label={i18nstring(strings.BADGE_ICON)}
                  iconSelected={icon => setIcon(icon)}
                />
              ) : (
                <div
                  style={{
                    display: "inline-block",
                    padding: "9px 10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    width: "auto",
                    position: "relative"
                  }}
                >
                  <span>{icon?.name || "badge_photo"}</span>
                  <Icon
                    name="remove circle"
                    size="large"
                    onClick={() => {
                      setIcon(null);
                      setUrl(null);
                    }}
                    style={{
                      position: "absolute",
                      right: "5px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer"
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="badge-input-parent">
            <div className="badge-input-container">
              {fieldLable(i18nstring(strings.BADGE_STATUS))}
              <div style={{ flexDirection: "row", display: "flex", gap: 15 }}>
                <Form.Field>
                  <Radio
                    label="Active"
                    name="status"
                    value="Active"
                    checked={formData.isActive === true}
                    onChange={(e, { value }) =>
                      handleChange(e, {
                        name: "isActive",
                        value: true
                      })
                    }
                  />
                </Form.Field>
                <Form.Field>
                  <Radio
                    label="Inactive"
                    name="status"
                    value="Inactive"
                    checked={formData.isActive === false}
                    onChange={(e, { value }) =>
                      handleChange(e, {
                        name: "isActive",
                        value: false
                      })
                    }
                  />
                </Form.Field>
              </div>
            </div>
          </div>
        </Form>
      </Modal.Content>
      <Button
        style={{ marginBottom: "2.5%", marginLeft: "2.5%" }}
        floated="left"
        onClick={() => resetFormData(false)}
      >
        {i18nstring(strings.CANCEL)}
      </Button>
      <Button
        style={{ marginBottom: "2.5%", marginRight: "2.5%", backgroundColor: "#5db99d", color: 'white' }}
        floated="right"
        onClick={submitRequest}
      >
        {i18nstring(strings.SAVE)}
      </Button>
    </Modal>
  );
}

const ModalLabel = ({ value }) => {
  return <label className="badge-input-label">{value}</label>;
};

const ModalInputField = ({label, nameKey, value, placeholder, handleChange}) => {
  return (
    <Form.Field>
      <ModalLabel value={label} />
      <Input
        name={nameKey}
        placeholder={placeholder}
        type="text"
        values={value}
        onChange={handleChange}
        style={{ width: "100%" }}
      />
    </Form.Field>
  );
};

const ModalDropDownField = ({showError, labelValue, options, value, handleChange, nameKey, placeholder, ...props}) => {
  return (
    <div style={{display: 'flex', width: '100%', flexDirection: 'column'}}>
      <FormSelect
        {...props}
        label={labelValue}
        name={nameKey}
        error={showError && value === ""}
        placeholder={placeholder}
        selection
        options={options}
        value={value}
        onChange={handleChange}
        style={{ width: "100%" }}
        search
      />
    </div>
  );
}

const ModalRadioGroup = ({showError, value, handleChange, nameKey, label1, radioVal1, label2, radioVal2}) => {

  return (
    <Form.Field
      error={showError && value === ""}
      className="form-field-donations"
    >
      <FormRadio
        className="form-field-donations"
        label={label1}
        name={nameKey}
        value={radioVal1}
        checked={value === radioVal1}
        onChange={(e, { name, value }) =>{
          handleChange(e, {
            name: nameKey,
            value: radioVal1
          })
        }}
      />
      <FormRadio
        label={label2}
        name={nameKey}
        value={radioVal2}
        checked={value === radioVal2}
        onChange={(e, { name, value }) =>{
          handleChange(e, {
            name: nameKey,
            value: radioVal2
          })
        }}
      />
    </Form.Field>
  );
};

export function PastDonationModal({
  gardeners,
  couriers,
  toggleModal,
  setToggle,
  selectedOption,
  subscriptions,
  addressesList,
  createDonation
}) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [formData, setFormData] = useState({
    gardener: "",
    courier: "",
    category: DonationCategory.DROPOFF,
    scheduledDate: '',
    donationTime: "AM",
    weight: 0,
    gardenerName: "",
    name: "",
    locationId: ""
  });
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedZip, setSelectedZip] = useState(null);
  const [isValidationClicked, setIsValidationClicked] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [gardenersList, setGardenerList] = useState([]);
  const [couriersList, setCouriersList] = useState([]);

  useEffect(() => {
    if (selectedOption === DonationType.UNLINED_DONATION && formData.category === DonationCategory.PICKUP) {
      const selectedAddress = addressesList?.filter(address => address.id === 'Contact');
      if (selectedAddress && selectedAddress.length > 0) {
        setSelectedLocationId(selectedAddress[0]?.id);
        setSelectedZip(selectedAddress[0].zips?.[0]);
      }
    }
  }, [selectedOption, addressesList, formData.category]);

  const locationOptions = addressesList?.map((address) => ({
    key: address.id,
    text: address.name,
    value: address.id
  }));

  useEffect(() => {
    if (selectedOption === DonationType.UNLINED_DONATION)
      return;
    const list =
      gardeners?.map((item, index) => ({
        id: item.id,
        name: item.name,
        email: item.email
      })) || [];
    setGardenerList(list);
  }, []);

    useEffect(() => {
      if (selectedOption === DonationType.UNLINED_DONATION)
        return;
      const list =
        couriers?.map((item, index) => ({
          id: item.id,
          name: item.name,
          email: item.email
        })) || [];
      setCouriersList(list);
    }, []);

  const selectedAddress = addressesList.find(
    address => address.id === selectedLocationId
  );

  const zipOptions =
    selectedAddress?.zips.map(zip => ({
      key: zip,
      text: zip,
      value: zip
    })) || [];

  const handleChange = (e, { name, value }) => {
    e.stopPropagation();
    setFormData({ ...formData, [name]: value });
  };

  const resetFormData = () => {
    setFormData({
      gardener: "",
      courier: "",
      donationTime: "AM",
      scheduledDate: "",
      category: DonationCategory.DROPOFF,
      weight: 0,
      name: "",
      locationId: ""
    });
    setSelectedLocationId("")
    setToggle(false);
    setIsValidationClicked(false)
  };

  const fieldLable = value => (
    <label className="badge-input-label">{value}</label>
  );

  const validateInput = () => {
    if (
      ((!selectedLocationId || selectedLocationId?.trim() === "") && formData.category === DonationCategory.DROPOFF) ||
      formData.category?.trim() === "" ||
      selectedZip === "" ||
      ((selectedOption === DonationType.LINKED_DONATION && formData.category === DonationCategory.PICKUP) && formData.courier?.trim() === "") ||
      ((selectedOption === DonationType.LINKED_DONATION) && formData.gardener?.trim() === "") ||
      selectedDate === "" ||
      formData.weight == "" || formData.weight <= 0
    ) {
      return false;
    }
    return true;
  };

  const submitData = () => {
    const dayOfWeek = selectedDate
      .toLocaleDateString("en-US", { weekday: "long" })
      .toUpperCase();

    const data = {
      isLinkedWithUser: selectedOption === DonationType.LINKED_DONATION,
      ...((selectedOption === DonationType.LINKED_DONATION && formData.gardener) && { producer: formData.gardener }),
      ...(formData.courier && { courier: formData.courier }),
      sourceType: "webapp_manual",
      name: formData.name?.trim() !== "" ? formData.name.trim() : "Garden Produce",
      category: formData.category,
      categoryDetails: formData.name?.trim() !== "" ? formData.name.trim() : "Garden Produce",
      weight: Number(formData.weight),
      notes: "No notes.",
      dateScheduled: selectedDate.getTime(),
      dayScheduled: dayOfWeek,
      timeBlockScheduled: formData.donationTime,
      repeats: "NONE",
      photoURL: "NONE",
      ...(selectedOption === DonationType.UNLINED_DONATION && { gardenerName: formData.gardenerName || "Unknown" }),
      ...(((selectedOption === DonationType.LINKED_DONATION && formData.category === DonationCategory.DROPOFF) || selectedOption === DonationType.UNLINED_DONATION) && {locationId: selectedLocationId}),
      ...((selectedOption === DonationType.UNLINED_DONATION) && {zip: selectedZip}),
    };
    createDonation(data);
    resetFormData();
    setToggle(false);
  }

  const getModalTitle = () => {
    return `${i18nstring(strings.ADD_PAST_DONATION)} - (${(selectedOption === 'linked' ? i18nstring(strings.LINKED_DONATION) : i18nstring(strings.UNLINKED_DONATION))})`;
  }

  return (
    <>
      <Modal style={{ width: 600 }} open={toggleModal}>
        <Modal.Header>{getModalTitle()}</Modal.Header>
        <Modal.Content>
          <Form>
            <div className="badge-input-parent">
              <div className="add-donation-input-container">
                <ModalLabel value={i18nstring(strings.CATEGORY)} />
                <div style={{ flexDirection: "row", display: "flex", gap: 15 }}>
                  <ModalRadioGroup
                    nameKey={"category"}
                    label1={i18nstring(strings.CATEGORY_DROPOFF)}
                    label2={i18nstring(strings.CATEGORY_PICKUP)}
                    radioVal1={DonationCategory.DROPOFF}
                    radioVal2={DonationCategory.PICKUP}
                    handleChange={handleChange}
                    showError={isValidationClicked}
                    value={formData.category}
                  />
                </div>
              </div>
            </div>

            <div className="badge-input-parent">
              <div className="add-donation-input-container">
                <Form.Field
                  className="add-donation-input-container"
                  error={isValidationClicked && selectedDate === ""}
                >
                  {fieldLable("Donation Date")}
                  <DatePicker
                    selected={selectedDate}
                    onChange={date => setSelectedDate(date)}
                    dateFormat="MMMM d, yyyy"
                    className="ui input"
                    maxDate={new Date()}
                    placeholderText="Select donation date"
                    showYearDropdown
                  />
                </Form.Field>
              </div>
            </div>
            {selectedOption === DonationType.LINKED_DONATION && (
              <>
                <div className="badge-input-parent">
                  <ModalDropDownField
                    labelValue={"Gardener"}
                    nameKey={"gardener"}
                    showError={isValidationClicked}
                    value={formData.gardener}
                    handleChange={handleChange}
                    placeholder="Select Gardener"
                    options={gardenersList.map((item, index) => ({
                      key: index,
                      text: `${item.name} (${item.email})`,
                      value: item.id,
                      content: `${item.name} (${item.email})`
                    }))}
                    fluid
                    search
                    selection
                  />
                </div>
                {formData.category === DonationCategory.PICKUP &&
                  selectedOption === DonationType.LINKED_DONATION && (
                    <>
                      <div className="badge-input-parent">
                        <ModalDropDownField
                          labelValue={"Courier"}
                          nameKey={"courier"}
                          showError={isValidationClicked}
                          value={formData.courier}
                          handleChange={handleChange}
                          placeholder="Select Courier"
                          options={couriersList.map((item, index) => ({
                            key: index,
                            text: `${item.name} (${item.email})`,
                            value: item.id,
                            content: `${item.name} (${item.email})`
                          }))}
                        />
                      </div>
                    </>
                  )}
              </>
            )}
            {((selectedOption === DonationType.UNLINED_DONATION &&
              formData.category !== DonationCategory.PICKUP) ||
              formData.category === DonationCategory.DROPOFF) && (
              <div className={"badge-input-parent"}>
                <FormSelect
                  label="Location"
                  style={{ flex: 1, width: "100%" }}
                  name="locationId"
                  placeholder="Select Location"
                  error={isValidationClicked && selectedLocationId === ""}
                  selection
                  options={locationOptions}
                  value={selectedLocationId}
                  onChange={(e, { name, value }) => {
                    setSelectedLocationId(value);
                    setSelectedZip(null);
                    handleChange(e, { name, value });
                  }}
                  search
                  disabled={formData.category === DonationCategory.PICKUP}
                />
                {(selectedOption === DonationType.UNLINED_DONATION &&
              formData.category !== DonationCategory.PICKUP) && (
                  <div>
                    <FormSelect
                    label="Zip"
                      name="Zip"
                      placeholder="Select Zip"
                      error={
                        isValidationClicked &&
                        (selectedZip === "" || selectedZip === null)
                      }
                      selection
                      options={zipOptions}
                      value={selectedZip}
                      onChange={(e, { name, value }) => {
                        setSelectedZip(value);
                        handleChange(e, { name, value });
                      }}
                      style={{ width: "100%", color: "black" }}
                      search
                      disabled={formData.category === DonationCategory.PICKUP}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="badge-input-parent">
              <div>
                <FormGroup >
                  <FormInput
                    label="Weight"
                    error={
                      isValidationClicked &&
                      (formData.weight == "" || formData.weight <= 0)
                    }
                    type="number"
                    placeholder={i18nstring(strings.WEIGHT_PLACEHOLDER)}
                    name="weight"
                    value={formData.weight}
                    onWheel={e => e.target.blur()}
                    onChange={handleChange}
                    style={{ flex: 1 }}
                    className="add-donation-input-container"
                  />
                  <label
                    className="badge-input-label"
                    style={{
                      justifyContent: "center",
                      marginLeft: 10,
                      marginRight: 10,
                      alignContent: "center",
                      marginTop: 20,
                      textAlign: 'center',
                      flex: 1,
                      alignItems: 'center'
                    }}
                  >
                    LBS
                  </label>
                </FormGroup>
              </div>
            </div>
            {selectedOption === DonationType.UNLINED_DONATION && (
              <div
                className="add-donation-input-parent"
                style={{ marginTop: 0, marginBottom: 10 }}
              >
                <div className="add-donation-input-container">
                  {fieldLable("Gardener Name (If known)")}
                  <Input
                    type="text"
                    placeholder={"Gardener Name (If known)"}
                    name="gardenerName"
                    value={formData.gardenerName}
                    onChange={handleChange}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>
            )}

            <div className="add-donation-input-parent">
              <div className="add-donation-input-container">
                {fieldLable("What's in the donation (Optional)")}
                <Form.TextArea
                  name="name"
                  placeholder={"Enter donation details"}
                  value={formData.name}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </Form>
        </Modal.Content>
        <Button
          style={{ marginBottom: "2.5%", marginLeft: "2.5%" }}
          floated="left"
          onClick={() => resetFormData(false)}
        >
          {i18nstring(strings.CANCEL)}
        </Button>
        <Button
          style={{
            marginBottom: "2.5%",
            marginRight: "2.5%",
            backgroundColor: "#5db99d",
            color: "white"
          }}
          floated="right"
          onClick={() => {
            setIsValidationClicked(true);
            if (!validateInput()) {
              return;
            }
            setShowConfirmationModal(true);
          }}
        >
          {i18nstring(strings.SAVE)}
        </Button>
      </Modal>
      {showConfirmationModal && (
        <ConfirmationPopup
          title={i18nstring(strings.CONFIRMATION)}
          message={i18nstring(strings.MANUAL_DONATION_CONFIRMATION_MESSAGE)}
          primaryButtonText={i18nstring(strings.CONFIRM)}
          onPrimaryButtonClicked={submitData}
          open={showConfirmationModal}
          setOpen={setShowConfirmationModal}
          secondaryButtonText={i18nstring(strings.GO_BACK)}
        />
      )}
    </>
  );
}


export function AddUserModal({ saveEmail, userEmail, userId, toggle, setToggle, existingEmails }) {
  const [inputs, setInputs] = useState({ emailAddress: userEmail || "" });
  const [error, setError] = useState("");

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleInputChange = event => {
    event.persist();
    setInputs(inputs => ({
      ...inputs,
      [event.target.name]: event.target.value
    }));
  };
  const send = () => {
    const emailAddress = inputs.emailAddress;
    if (existingEmails.some(e => emailAddress.trim().toLowerCase() === e.trim().toLowerCase())) {
      setError(i18nstring(strings.EMAIL_ALREADY_EXIST));
      return;
    }
    saveEmail(userId, inputs.emailAddress);
  };
  return (
    <Modal style={{ width: 550, maxWidth: "90vw" }} open={toggle}>
      <Modal.Header style={{ textAlign: "center", padding: "20px" }}>
        {i18nstring(strings.ADD_EMAIL_ADDRESS)}
      </Modal.Header>
      <Modal.Content style={{ padding: "20px 30px" }}>
        <Form>
          <Form.Field>
            <label className="badge-input-label">
              {i18nstring(strings.EMAIL)}
            </label>
            <Input
              name="emailAddress"
              placeholder={i18nstring(strings.ENTER_EMAIL_ADDRESS)}
              onChange={handleInputChange}
              fluid
              value={inputs.emailAddress}
              type="email"
              style={{ fontSize: "14px" }}
            />
          </Form.Field>
          {error && (
            <div
              style={{ color: "#db2828", fontSize: "12px", marginTop: "4px" }}
            >
              {error}
            </div>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          negative
          onClick={() => setToggle(false)}
          style={{ backgroundColor: Colors.lightGrey }}
        >
          {i18nstring(strings.CANCEL)}
        </Button>
        <Button
          positive
          onClick={send}
          disabled={
            !inputs.emailAddress ||
            !inputs.emailAddress.trim() ||
            !isValidEmail(inputs.emailAddress)
          }
          style={{
            backgroundColor: Colors.greyishTeal,
            color: "#fff"
          }}
        >
          {i18nstring(strings.ADD)}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}